import { FileIcon, useDownloadFile } from '@newfront-insurance/admin-ui/files';
import type { novaToolDefs } from '@newfront-insurance/ai-api';
import { cn } from '@newfront-insurance/core-ui/v2';
import type { InferUITool, ToolUIPart } from 'ai';

type ProcessFilesOutput = InferUITool<(typeof novaToolDefs)['processFiles']>['output'];

/**
 * Helper function to render generated files from tool output
 */
function renderGeneratedFiles(output: ProcessFilesOutput): React.ReactNode | null {
  if (typeof output === 'string') return null;

  // Use generated files from Files service
  if (output.generatedFiles && output.generatedFiles.length > 0) {
    const generatedFiles = output.generatedFiles.map((file) => ({
      uuid: file.fileUuid,
      filename: file.filename,
    }));
    return <GeneratedFilesList files={generatedFiles} />;
  }

  // Fallback to generatedFileUuids
  if (output.generatedFileUuids && output.generatedFileUuids.length > 0) {
    const generatedFiles = output.generatedFileUuids.map((uuid: string, idx: number) => ({
      uuid,
      filename: `Generated file ${idx + 1}`,
    }));
    return <GeneratedFilesList files={generatedFiles} />;
  }

  return null;
}

interface ProcessFilesToolProps {
  part: ToolUIPart;
}

/**
 * Component for rendering processFiles tool output.
 */
export function ProcessFilesTool({ part }: ProcessFilesToolProps) {
  if (part.state === 'output-available') {
    return renderGeneratedFiles(part.output as ProcessFilesOutput);
  }

  return null;
}

export function GeneratedFilesList({ files }: { files: { uuid: string; filename: string }[] }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-muted-foreground text-sm font-medium">Generated Files:</div>
      <div className="flex flex-row flex-wrap gap-2">
        {files.map((file) => (
          <ChatFileListItem key={file.uuid} file={file} />
        ))}
      </div>
    </div>
  );
}

function ChatFileListItem({ file }: { file: { uuid: string; filename: string } }) {
  const { download, isDownloading } = useDownloadFile({});

  const handleClick = () => {
    download(file.uuid);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'flex w-fit max-w-xs cursor-pointer items-center gap-2 rounded-sm px-1 py-1',
        'hover:bg-muted/50 transition-colors',
        { 'pointer-events-none opacity-50': isDownloading },
      )}
    >
      <FileIcon filename={file.filename} hasBackground={false} />
      <span className="text-steel-400 truncate text-xs">{file.filename}</span>
    </div>
  );
}
